/**
 * 수료증 화면/인쇄용 컴포넌트 + PDF(캔버스 → JPEG → PDF) 생성
 *  - 인쇄: CertificateSheet 를 A4 가로로 나열 후 window.print()
 *  - PDF: 같은 좌표로 캔버스에 그려 외부 라이브러리 없이 PDF 파일 생성
 */
import React from 'react';
import type { CertData } from '../api';
import { certDate, downloadBlob } from '../common';
import './certificate.css';

export const CERT_BG: Record<string, string> = {
  '01': '/bt-cert/bt-cert-01-puggles-cubbies.jpg',
  '02': '/bt-cert/bt-cert-02-sparks-tt.jpg',
  '03': '/bt-cert/bt-cert-03-trek.jpg',
  '04': '/bt-cert/bt-cert-04-journey.jpg',
};

export const certName = (cert: CertData) => {
  const name = (cert.teacherNameEn || '').trim().toUpperCase();
  return /^[A-Z][A-Z .'-]*$/.test(name) ? name : '';
};

export const CertificateView: React.FC<{ cert: CertData; fluid?: boolean }> = ({ cert, fluid }) => (
  <div className={`bt-cert${fluid ? ' bt-cert-fluid' : ''}`}>
    <img className="bg" src={CERT_BG[cert.bg] || CERT_BG['02']} alt="" />
    <img className="sig" src={cert.signatureUrl || '/bt-cert/sig-default.png'} alt="" />
    <div className="f serial">{cert.serial}</div>
    <div className="f name">{certName(cert)}</div>
    <div className="f church">{cert.churchName}</div>
    <div className="f date">{certDate(cert.completedAt)}</div>
  </div>
);

export const CertificateSheet: React.FC<{ cert: CertData }> = ({ cert }) => (
  <div className="bt-cert-page"><CertificateView cert={cert} /></div>
);

/** 인쇄 전에 이미지 로드 대기 */
export const waitImages = async (root: ParentNode = document) => {
  await Promise.all(Array.from(root.querySelectorAll('img')).map((img) => (
    img.complete ? null : new Promise((r) => { img.onload = r; img.onerror = r; })
  )));
  if ((document as any).fonts?.ready) await (document as any).fonts.ready;
};

// ───────── 캔버스 렌더링 ─────────
const loadImage = (src: string) => new Promise<HTMLImageElement | null>((resolve) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => resolve(null);
  img.src = src;
});

const FONT = 'Calibri, Carlito, "Noto Sans KR", "Malgun Gothic", "Apple SD Gothic Neo", sans-serif';

export const renderCertificateCanvas = async (cert: CertData, width = 2600) => {
  if (!certName(cert)) throw new Error(`${cert.teacherName} 선생님의 영문 이름을 교사 정보에서 입력해주세요.`);
  const height = Math.round((width * 540) / 780);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, width, height);
  const [bg, sig] = await Promise.all([loadImage(CERT_BG[cert.bg] || CERT_BG['02']), loadImage(cert.signatureUrl || '/bt-cert/sig-default.png')]);
  if (bg) ctx.drawImage(bg, 0, 0, width, height);
  if (sig) {
    // object-fit: contain, 아래 가운데 정렬
    const bx = width * 0.15; const by = height * 0.726; const bw = width * 0.18; const bh = height * 0.104;
    const scale = Math.min(bw / sig.width, bh / sig.height);
    const w = sig.width * scale; const h = sig.height * scale;
    ctx.drawImage(sig, bx + (bw - w) / 2, by + bh - h, w, h);
  }
  const cqw = width / 100;
  const field = (text: string, leftPct: number, topPct: number, widthPct: number | null, sizeCqw: number, bold: boolean, align: 'left' | 'center', spacing = 0) => {
    const px = sizeCqw * cqw;
    ctx.font = `${bold ? '700 ' : ''}${px}px ${FONT}`;
    ctx.fillStyle = '#000';
    ctx.textBaseline = 'top';
    if ('letterSpacing' in ctx && spacing) (ctx as any).letterSpacing = `${spacing * px}px`;
    else if ('letterSpacing' in ctx) (ctx as any).letterSpacing = '0px';
    const y = (topPct / 100) * height + 0.46 * cqw + px * 0.1;
    const left = (leftPct / 100) * width;
    if (align === 'center' && widthPct) {
      const w = ctx.measureText(text).width;
      ctx.fillText(text, left + ((widthPct / 100) * width - w) / 2, y);
    } else {
      ctx.fillText(text, left, y);
    }
  };
  field(cert.serial, 78.0, 18.37, null, 1.7, false, 'left');
  field(certName(cert), 24.3, 44.97, 51.78, 3.95, true, 'center', 0.03);
  field(cert.churchName, 15.54, 63.51, 24.62, 2.25, true, 'center');
  field(certDate(cert.completedAt).replace(/ /g, '  '), 59.84, 64.01, 24.62, 2.45, false, 'center');
  return canvas;
};

// ───────── JPEG → PDF (A4 가로, 1장 1페이지) ─────────
const enc = new TextEncoder();
const concat = (parts: Uint8Array[]) => {
  const total = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let o = 0;
  parts.forEach((p) => { out.set(p, o); o += p.length; });
  return out;
};

export const jpegsToPdf = (pages: Array<{ jpeg: Uint8Array; width: number; height: number }>) => {
  const PW = 841.89; const PH = 595.28;                 // A4 가로 (pt)
  const certW = (281 / 297) * PW; const certH = certW * (540 / 780);
  const x = (PW - certW) / 2; const y = (PH - certH) / 2;
  const chunks: Uint8Array[] = [];
  const offsets: number[] = [];
  let length = 0;
  const push = (data: Uint8Array | string) => {
    const bytes = typeof data === 'string' ? enc.encode(data) : data;
    chunks.push(bytes);
    length += bytes.length;
  };
  const obj = (id: number, body: (Uint8Array | string)[]) => {
    offsets[id] = length;
    push(`${id} 0 obj\n`);
    body.forEach(push);
    push('\nendobj\n');
  };
  push('%PDF-1.4\n%\xE2\xE3\xCF\xD3\n');
  const n = pages.length;
  // 1 catalog, 2 pages, 이후 페이지마다 (page, image, content) 3개
  const pageIds = pages.map((_, i) => 3 + i * 3);
  obj(1, ['<< /Type /Catalog /Pages 2 0 R >>']);
  obj(2, [`<< /Type /Pages /Count ${n} /Kids [${pageIds.map((id) => `${id} 0 R`).join(' ')}] >>`]);
  pages.forEach((p, i) => {
    const pageId = pageIds[i]; const imgId = pageId + 1; const contentId = pageId + 2;
    obj(pageId, [`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PW} ${PH}] /Resources << /XObject << /Im${i} ${imgId} 0 R >> >> /Contents ${contentId} 0 R >>`]);
    obj(imgId, [`<< /Type /XObject /Subtype /Image /Width ${p.width} /Height ${p.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${p.jpeg.length} >>\nstream\n`, p.jpeg, '\nendstream']);
    const content = `q ${certW.toFixed(2)} 0 0 ${certH.toFixed(2)} ${x.toFixed(2)} ${y.toFixed(2)} cm /Im${i} Do Q`;
    obj(contentId, [`<< /Length ${content.length} >>\nstream\n${content}\nendstream`]);
  });
  const total = 3 + n * 3;
  const xref = length;
  let table = `xref\n0 ${total}\n0000000000 65535 f \n`;
  for (let i = 1; i < total; i += 1) table += `${String(offsets[i]).padStart(10, '0')} 00000 n \n`;
  push(table);
  push(`trailer\n<< /Size ${total} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`);
  return new Blob([concat(chunks)], { type: 'application/pdf' });
};

const canvasJpeg = (canvas: HTMLCanvasElement) => new Promise<Uint8Array>((resolve, reject) => {
  canvas.toBlob(async (blob) => {
    if (!blob) return reject(new Error('이미지 변환 실패'));
    resolve(new Uint8Array(await blob.arrayBuffer()));
  }, 'image/jpeg', 0.92);
});

/** 수료증 여러 장 → PDF 1개 다운로드 */
export const downloadCertificatesPdf = async (certs: CertData[], filename?: string) => {
  if ((document as any).fonts?.ready) await (document as any).fonts.ready;
  const pages = [];
  for (const cert of certs) {
    const canvas = await renderCertificateCanvas(cert);
    pages.push({ jpeg: await canvasJpeg(canvas), width: canvas.width, height: canvas.height });
  }
  const name = filename || (certs.length === 1 ? `BT수료증_${certs[0].serial}_${certs[0].teacherName}.pdf` : `BT수료증_${certs.length}장.pdf`);
  downloadBlob(jpegsToPdf(pages), name);
};
